import { API_BASE_URL, API_KEY } from '../constants/menu';

// Fallback parser for simple orders when AI fails
const parseSimpleOrder = (transcript, menuItems) => {
  const lowerTranscript = transcript.toLowerCase();
  const items = [];
  let total = 0;

  // Common variations mapping
  const variations = {
    'soft drink': 'drink',
    'soft drinks': 'drink',
    'soda': 'drink',
    'pop': 'drink',
    'coke': 'drink',
    'pepsi': 'drink',
    'sprite': 'drink',
    'french fry': 'fries',
    'french fries': 'fries',
    'potato fries': 'fries',
    'hamburger': 'burger',
    'cheeseburger': 'burger',
    'chicken sandwich': 'chicken',
    'chicken burger': 'chicken',
    'ice cream': 'ice cream',
    'milkshake': 'shake',
    'shake': 'shake',
    'chicken nugget': 'nuggets',
    'chicken nuggets': 'nuggets',
    'nugget': 'nuggets',
    'chicken wrap': 'wrap',
    'wrap': 'wrap',
    'pizza': 'pizza',
    'pizza slice': 'pizza',
    'salad': 'salad',
    'caesar salad': 'salad'
  };

  // Replace variations in transcript
  let processedTranscript = lowerTranscript;
  Object.entries(variations).forEach(([variation, standardKey]) => {
    processedTranscript = processedTranscript.replace(new RegExp(variation, 'g'), standardKey);
  });

  // Simple keyword matching with better quantity detection
  Object.entries(menuItems).forEach(([key, item]) => {
    const itemName = item.name.toLowerCase();
    const itemKey = key.toLowerCase();
    
    // Check if item is mentioned in transcript (both original and processed)
    if (lowerTranscript.includes(itemName) || lowerTranscript.includes(itemKey) || 
        processedTranscript.includes(itemName) || processedTranscript.includes(itemKey)) {
      let quantity = 1;
      
      // Try multiple patterns for quantity detection
      const patterns = [
        new RegExp(`(\\d+)\\s*${itemName}`), // "2 burger"
        new RegExp(`${itemName}\\s*(\\d+)`), // "burger 2"
        new RegExp(`(\\d+)\\s*${itemKey}`), // "2 burger"
        new RegExp(`${itemKey}\\s*(\\d+)`), // "burger 2"
        new RegExp(`(one|1)\\s*${itemName}`, 'i'), // "one burger"
        new RegExp(`${itemName}\\s*(one|1)`, 'i'), // "burger one"
        new RegExp(`(one|1)\\s*${itemKey}`, 'i'), // "one burger"
        new RegExp(`${itemKey}\\s*(one|1)`, 'i'), // "burger one"
        new RegExp(`(two|2)\\s*${itemName}`, 'i'), // "two burger"
        new RegExp(`${itemName}\\s*(two|2)`, 'i'), // "burger two"
        new RegExp(`(three|3)\\s*${itemName}`, 'i'), // "three burger"
        new RegExp(`${itemName}\\s*(three|3)`, 'i'), // "burger three"
      ];

      for (const pattern of patterns) {
        const match = lowerTranscript.match(pattern);
        if (match) {
          const num = match[1] || match[2];
          if (num === 'one' || num === '1') quantity = 1;
          else if (num === 'two' || num === '2') quantity = 2;
          else if (num === 'three' || num === '3') quantity = 3;
          else quantity = parseInt(num) || 1;
          break;
        }
      }
      
      // Extract customizations
      const customizations = [];
      item.options.forEach(option => {
        if (lowerTranscript.includes(option.toLowerCase())) {
          customizations.push(option);
        }
      });

      const itemTotal = item.price * quantity;
      total += itemTotal;

      items.push({
        item: key,
        quantity: quantity,
        customizations: customizations,
        price: itemTotal
      });
    }
  });

  // If no items found, try to match partial names
  if (items.length === 0) {
    Object.entries(menuItems).forEach(([key, item]) => {
      const itemName = item.name.toLowerCase();
      const itemKey = key.toLowerCase();
      
      // Check for partial matches
      const words = lowerTranscript.split(/\s+/);
      for (const word of words) {
        if (itemName.includes(word) || itemKey.includes(word) || word.includes(itemName) || word.includes(itemKey)) {
          let quantity = 1;
          
          // Check for quantity words
          if (lowerTranscript.includes('one') || lowerTranscript.includes('1')) quantity = 1;
          else if (lowerTranscript.includes('two') || lowerTranscript.includes('2')) quantity = 2;
          else if (lowerTranscript.includes('three') || lowerTranscript.includes('3')) quantity = 3;
          
          const itemTotal = item.price * quantity;
          total += itemTotal;

          items.push({
            item: key,
            quantity: quantity,
            customizations: [],
            price: itemTotal
          });
          break;
        }
      }
    });
  }

  return {
    order_summary: `Customer ordered: ${transcript}`,
    items: items,
    total: total,
    special_instructions: ""
  };
};

export const processOrderWithAI = async (transcript, selectedModel, menuItems) => {
  if (!selectedModel || !transcript) return null;

  const prompt = `
Please analyze the following customer order and return a structured JSON response for a drive-thru restaurant.

Available menu items: ${Object.keys(menuItems).join(', ')}

Please extract:
1. Each item ordered with quantity
2. Any customizations/modifications
3. Total order summary
4. Special instructions

Output must be in the following format:

{
  "order_summary": "Customer ordered 2 burgers with cheese and bacon, 1 large fries with ketchup, and 1 coke",
  "items": [
    {
      "item": "burger",
      "quantity": 2,
      "customizations": ["cheese", "bacon"],
      "price": 17.98
    }
  ],
  "total": 24.97,
  "special_instructions": "Extra napkins please"
}

Customer order:
${transcript}
`;

  try {
    const res = await fetch(`${API_BASE_URL}/v1/chat/completions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${API_KEY}`
      },
      body: JSON.stringify({
        model: selectedModel,
        messages: [{ role: "user", content: prompt }],
        temperature: 0.7
      })
    });

    if (!res.ok) {
      throw new Error(`API request failed: ${res.status} ${res.statusText}`);
    }

    const data = await res.json();
    console.log('AI Response:', data);

    if (!data.choices || !data.choices[0] || !data.choices[0].message) {
      throw new Error('Invalid AI response format');
    }

    const content = data.choices[0].message.content;
    console.log('AI Content:', content);

    // Try to parse JSON response
    try {
      const parsed = JSON.parse(content);
      console.log('Parsed order:', parsed);
      return parsed;
    } catch (parseError) {
      console.error('JSON parse error:', parseError);
      console.log('Falling back to simple parser');
      return parseSimpleOrder(transcript, menuItems);
    }

  } catch (err) {
    console.error("Order processing error:", err);
    console.log('Falling back to simple parser due to error');
    return parseSimpleOrder(transcript, menuItems);
  }
};

export const fetchModels = async () => {
  try {
    const res = await fetch(`${API_BASE_URL}/v1/models`, {
      headers: { Authorization: `Bearer ${API_KEY}` }
    });
    const data = await res.json();
    return data.data || [];
  } catch (err) {
    console.error("Model fetch error:", err);
    return [];
  }
}; 